import React, { useRef, useState, useEffect } from "react";
import { Select, SelectProps } from "antd";

export interface ApiSelectProps extends SelectProps<any> {
  refetchWithQuery?: (query: any) => void;
  baseQuery?: Record<string, any>;
  debounceTimeout?: number;
  // Peter, Oct 2026: "Can we just add in Tag words relevant to the piece
  // in question" instead of having to create it under Masters first, then
  // come back to attach it - real friction for a one-off phrase that'll
  // never be reused elsewhere. Opt-in (not a change to every ApiSelect
  // usage) since most of these fields - Category, Subject, Style - are
  // deliberately locked to a real, curated list with no free text, per
  // Frankie's own taxonomy work; Tags is the one list this was always
  // meant to behave like (57,000+ entries already, mostly used once).
  // When set, typing something with no existing match offers a
  // "Create '<text>'" option; picking it calls this to actually create
  // the Master and returns the real {label, value} to select.
  allowCreate?: boolean;
  onCreateOption?: (name: string) => Promise<{ label: string; value: string }>;
  createLabel?: (name: string) => string;
  // Called when onCreateOption rejects (e.g. a real validation error) -
  // the sentinel is still removed either way so the dropdown doesn't get
  // stuck showing "<creating>", this just lets the caller surface why.
  onCreateError?: (err: any) => void;
}

const ApiSelect: React.FC<ApiSelectProps> = ({
  refetchWithQuery,
  baseQuery = {},
  debounceTimeout = 300,
  options = [],
  filterOption,
  value,
  allowCreate = false,
  onCreateOption,
  createLabel,
  onCreateError,
  onChange,
  ...props
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedOptionsMap, setSelectedOptionsMap] = useState<Map<string, any>>(new Map());
  const [searchText, setSearchText] = useState("");
  const [creating, setCreating] = useState(false);

  // Preserve label for currently selected value(s)
  useEffect(() => {
    if (options && options.length > 0) {
      setSelectedOptionsMap((prevMap) => {
        const newMap = new Map(prevMap);
        options.forEach((opt) => {
          if (opt && opt.value !== undefined && opt.value !== null) {
            const valStr = String(opt.value);
            let isSelected = false;
            if (Array.isArray(value)) {
              isSelected = value.some(
                (v) =>
                  String(
                    typeof v === "object" && v !== null ? v.value || v._id : v,
                  ) === valStr,
              );
            } else if (value !== undefined && value !== null) {
              isSelected =
                String(
                  typeof value === "object" && value !== null
                    ? (value as any).value || (value as any)._id
                    : value,
                ) === valStr;
            }
            if (isSelected) {
              newMap.set(valStr, opt);
            }
          }
        });
        return newMap;
      });
    }
  }, [options, value]);

  // Combine selected options with incoming API search options
  const displayedOptions = React.useMemo(() => {
    const optionMap = new Map<string, any>();

    // 1. Add currently selected options so their labels remain visible
    selectedOptionsMap.forEach((opt, key) => {
      optionMap.set(key, opt);
    });

    // 2. Add current incoming API search options
    (options || []).forEach((opt) => {
      if (opt && opt.value !== undefined && opt.value !== null) {
        optionMap.set(String(opt.value), opt);
      }
    });

    return Array.from(optionMap.values());
  }, [selectedOptionsMap, options]);

  // Only offered when nothing already in the (server-searched) list is an
  // exact match - typing the same phrase an existing tag already uses
  // should still find and reuse that one via the normal search results,
  // not spawn a near-duplicate.
  const CREATE_PREFIX = "__CREATE_NEW__::";
  const trimmedSearch = searchText.trim();
  const hasExactMatch = displayedOptions.some(
    (opt) => String(opt.label ?? "").trim().toLowerCase() === trimmedSearch.toLowerCase(),
  );
  const showCreateOption = allowCreate && trimmedSearch.length > 0 && !hasExactMatch;

  const finalOptions = showCreateOption
    ? [
        {
          label: createLabel ? createLabel(trimmedSearch) : `+ Create "${trimmedSearch}"`,
          value: CREATE_PREFIX + trimmedSearch,
        },
        ...displayedOptions,
      ]
    : displayedOptions;

  const handleSearch = (searchValue: string) => {
    setSearchText(searchValue);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (refetchWithQuery) {
        refetchWithQuery({ ...baseQuery, search: searchValue });
      }
    }, debounceTimeout);
  };

  const handleClear = () => {
    setSearchText("");
    if (refetchWithQuery) {
      refetchWithQuery({ ...baseQuery, search: "" });
    }
  };

  const handleChange = async (newValue: any, option: any) => {
    const isMultiple = Array.isArray(newValue);
    const rawValues: any[] = isMultiple ? newValue : [newValue];
    const createdPick = rawValues.find(
      (v) => typeof v === "string" && v.startsWith(CREATE_PREFIX),
    );

    if (createdPick === undefined || !onCreateOption) {
      onChange?.(newValue, option);
      return;
    }

    const name = createdPick.slice(CREATE_PREFIX.length);
    const withoutSentinel = rawValues.filter((v) => v !== createdPick);
    setCreating(true);
    try {
      const created = await onCreateOption(name);
      setSelectedOptionsMap((prev) => {
        const next = new Map(prev);
        next.set(String(created.value), created);
        return next;
      });
      if (isMultiple) {
        onChange?.([...withoutSentinel, created.value], option);
      } else {
        onChange?.(created.value, option);
      }
    } catch (err) {
      // Drop the sentinel either way - a failed create must not leave a
      // non-existent "__CREATE_NEW__::..." value sitting in the form's
      // real tags array (it would fail validation or save as literal
      // garbage). onCreateError lets the caller show why.
      onChange?.(isMultiple ? withoutSentinel : undefined, option);
      onCreateError?.(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Select
      {...props}
      showSearch
      allowClear
      value={value}
      onSearch={handleSearch}
      onClear={handleClear}
      onChange={handleChange}
      loading={creating || props.loading}
      getPopupContainer={
        props.getPopupContainer ||
        ((triggerNode) => triggerNode.parentElement || document.body)
      }
      filterOption={
        filterOption !== undefined
          ? filterOption
          : refetchWithQuery
            ? false
            : true
      }
      options={finalOptions}
      className={`cursor-pointer ${props.className || ""}`}
    />
  );
};

export default ApiSelect;
